import { z } from 'zod';
import { ComplianceVerificationSchema } from '../lib/enums';
import { zIsoDateTime, zUuid } from './common';
/**
 * Tony — the in-product assistant.
 *
 * THE SHAPE ENFORCES THE BOUNDARY. This is the point of the file, so it is
 * worth stating plainly:
 *
 *  - An answer that draws on compliance content MUST carry its citations, and
 *    a compliance citation has a NON-OPTIONAL `boundaryNote` and
 *    `verification`. There is no way to construct one without them, mirroring
 *    the `NOT NULL` on `compliance_rules.boundary_note` and the CHECK on
 *    `rag_chunks`. A caller cannot drop the disclaimer to save space.
 *
 *  - Product law (docs/ARCHITECTURE.md rule 3): compliance content is
 *    informational tooling, never advice, and the product NEVER asserts that
 *    anything "is compliant". Tony inherits that verbatim. There is
 *    deliberately no `confidence`, no `verdict`, and no `compliant` field
 *    anywhere below — there is nothing to put such a claim in.
 *
 *  - Retrieval is audience-scoped. The CONTRACTOR-facing library shows
 *    `unverified_draft` content, loudly labelled; the CLIENT-facing proposal
 *    renderer admits only `verified` / `federal_anchor`. `TonyAudience` carries
 *    that distinction so a retrieval cannot forget which side it is serving.
 */
/** Which side of the product is asking. Decides what verification levels reach the answer. */
export const TonyAudienceSchema = z.enum(['contractor', 'client']);
export const TonySourceKindSchema = z.enum(['product_help', 'compliance_rule', 'external']);
/**
 * One cited passage behind an answer. Rendered with the answer, never
 * summarised away — a claim without its source is exactly what this product
 * refuses to make.
 */
export const TonyCitationSchema = z
    .object({
    documentId: zUuid,
    chunkId: zUuid,
    sourceKind: TonySourceKindSchema,
    title: z.string().min(1).max(300),
    /** Where a human goes to read the original. Null for content with no canonical URL. */
    uri: z.string().max(2000).nullable(),
    /** The passage actually used, so a reader can check the answer against it. */
    excerpt: z.string().min(1).max(4000),
    /**
     * Present for compliance content, null for product help. When present it is
     * rendered as a badge — never hidden, never collapsed.
     */
    verification: ComplianceVerificationSchema.nullable(),
    /**
     * Rendered verbatim beside any compliance answer. Null only for sources
     * that have no such notion (product documentation).
     */
    boundaryNote: z.string().max(2000).nullable(),
    jurisdiction: z.string().length(2).nullable(),
})
    .strict()
    .superRefine((value, ctx) => {
    if (value.sourceKind !== 'compliance_rule')
        return;
    if (value.boundaryNote === null || value.boundaryNote.trim() === '') {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['boundaryNote'],
            message: 'compliance content must carry its boundary note — it is informational tooling, never advice',
        });
    }
    if (value.verification === null) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['verification'],
            message: 'compliance content must carry its verification label',
        });
    }
});
/** Optional hint about where the user is, so an answer can be about what they are looking at. */
export const TonyContextSchema = z
    .object({
    /** Panel route or mobile screen the question was asked from. */
    route: z.string().max(200).optional(),
    /** The job's jurisdiction, when there is one — answers are state-specific. */
    jurisdiction: z.string().length(2).optional(),
})
    .strict();
export const AskTonyRequestSchema = z
    .object({
    question: z.string().trim().min(1).max(4000),
    context: TonyContextSchema.optional(),
    /** Client-generated id so a retried send cannot double-post. */
    clientRequestId: z.string().min(1).max(64).optional(),
})
    .strict();
export const TonyReplySchema = z.object({
    /**
     * FALSE while no model is wired. The UI must say so plainly rather than
     * render `answer` as though a model produced it — a fabricated answer in a
     * product that prices real work is worse than no answer.
     */
    modelConnected: z.boolean(),
    /** Human-readable reply. When `modelConnected` is false this explains that. */
    answer: z.string().max(20000),
    /** What the answer was drawn from. May be non-empty even when no model is connected. */
    citations: z.array(TonyCitationSchema),
    audience: TonyAudienceSchema,
    askedAt: zIsoDateTime,
});
/**
 * The tappable prompts shown above the composer. Served by the API so the panel
 * and the mobile app cannot drift apart, and so they can later be tuned per
 * route without shipping either client.
 */
export const TonySuggestionSchema = z
    .object({
    id: z.string().min(1).max(64),
    /** Shown in the bubble. Keep short — a mobile chip is one line. */
    label: z.string().min(1).max(80),
    /** Sent as the question when tapped; may be longer than the label. */
    question: z.string().min(1).max(400),
})
    .strict();
export const TonySuggestionsResponseSchema = z.object({
    suggestions: z.array(TonySuggestionSchema),
});
// ------------------------------------------------------------------ help
/** One article in the Help centre. The same corpus the RAG index is built from. */
export const HelpArticleSummarySchema = z.object({
    slug: z.string().min(1).max(120),
    title: z.string().min(1).max(200),
    summary: z.string().max(500),
    category: z.string().min(1).max(80),
});
export const HelpArticleSchema = HelpArticleSummarySchema.extend({
    /** Markdown, rendered by the panel's existing dependency-free renderer. */
    body: z.string().min(1),
});
export const HelpIndexResponseSchema = z.object({
    articles: z.array(HelpArticleSummarySchema),
});
//# sourceMappingURL=tony.js.map